import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import '../../../../../../sass/apps/commerce/forms/stripe.scss';
import '../../../../../../sass/apps/commerce/forms/paypal.scss';
import PaymentProviderIcons from '../../../components/payment_provider_icons';
import options from './card_form_config';
import { PaymentErrorObject, PaymentProvider } from '../../CheckoutTypes';

export interface StripeCardProps {
  autoFocus?: boolean;
  buttonText?: string;
  formClass?: string;
  staticFilesServer: string;
  // beforeSubmitErrorMessage is for validation just before we call the payment provider
  beforeSubmitErrorMessage?: (paymentProvider: PaymentProvider) => string;
  // this need better typing once the api docs have been updated
  onSuccess: (id: string, subtype?: any, invoice?: any) => any;
  titleText?: string;
  buttonClassModifier?: string;
  localisations: {
    [key: string]: string;
  };
}

let defaultTitleText = '';

function CardForm({
  onSuccess,
  autoFocus = false,
  buttonText,
  formClass = 'stripe-form',
  staticFilesServer,
  beforeSubmitErrorMessage = () => '',
  titleText = defaultTitleText,
  buttonClassModifier = 'button--wide',
  localisations,
}: StripeCardProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');
  const [isDisabled, setIsDisabled] = useState(true);
  const [isMakingPayment, setIsMakingPayment] = useState(false);
  defaultTitleText = localisations.enter_card_details;
  buttonText = buttonText == '' ? localisations.pay : buttonText;

  function stripeReady(element) {
    if (autoFocus) {
      element.focus();
    }
    setIsDisabled(false);
  }

  function checkForErrors({ error }) {
    setErrorMessage(error ? error.message : '');
  }

  async function handleAuthorisationResponse(client_secret) {
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      // stripe not loaded error message?
      return false;
    }
    const {
      paymentIntent,
      error: paymentError,
    } = await stripe.handleCardAction(client_secret);
    if (paymentError) {
      setErrorMessage(paymentError.message);
      return setIsMakingPayment(false);
    }
    if (paymentIntent) {
      return onSuccess(paymentIntent.id);
    }
    return false;
  }
  async function handleSubscriptionResponse(
    client_secret,
    subtype?: any,
    invoice?: any,
  ) {
    if (!stripe) {
      return false;
    }
    const {
      paymentIntent,
      error: paymentError,
    } = await stripe.confirmCardPayment(client_secret);
    if (paymentError) {
      setErrorMessage(paymentError.message);
      return setIsMakingPayment(false);
    }
    if (paymentIntent) {
      return onSuccess(paymentIntent.id, subtype, invoice);
    }
    // this probably needs sorting out.
    return false;
  }

  async function handleApiResponse({
    actionRequired,
    client_secret,
    subscription,
    invoice,
    message,
    paymentProvider,
    paymentProviderSubType,
  }: PaymentErrorObject) {
    message = !message ? localisations.sorry_something_went_wrong : message;
    if (paymentProvider != 'stripe' || !stripe) {
      setIsMakingPayment(false);
      return false;
    }
    if (actionRequired) {
      if (client_secret) {
        if (subscription) {
          return handleSubscriptionResponse(
            client_secret,
            paymentProviderSubType,
            invoice,
          );
        }
        return handleAuthorisationResponse(client_secret);
      }
    }
    setErrorMessage(message);
    return setIsMakingPayment(false);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (isMakingPayment) {
      return false;
    }
    setIsMakingPayment(true);
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      // stripe not loaded error message?
      return setIsMakingPayment(false);
    }
    const isNotValidMessage = beforeSubmitErrorMessage('stripe');
    if (isNotValidMessage) {
      setErrorMessage(isNotValidMessage);
      return setIsMakingPayment(false);
    }
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      // should set error message?
      return setIsMakingPayment(false);
    }
    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });
    if (error) {
      setErrorMessage(error.message);
      return setIsMakingPayment(false);
    }
    if (paymentMethod) {
      const response = await onSuccess(paymentMethod.id);
      if (response) {
        return handleApiResponse(response);
      }
      return true;
    }
    // what are we doing here = should we provide a message?
    return setIsMakingPayment(false);
  }

  return (
    <form
      className={formClass}
      onSubmit={(event) => handleSubmit(event)}
      id="stripeForm"
    >
      <PaymentProviderIcons
        className={'stripe-icons'}
        staticFilesServer={staticFilesServer}
        paymentProviders={[{ name: 'stripe' }]}
      />

      <div className="form-row">
        <label
          className={`stripe-form__card-label${
            titleText === '' ? ' sr-only' : ''
          }`}
          htmlFor="card-element"
        >
          {titleText === '' ? defaultTitleText : titleText}
        </label>
        <div id="stripeFormCardElement">
          <CardElement
            onChange={(event) => checkForErrors(event)}
            onReady={(element) => stripeReady(element)}
            options={options}
            // classes={classes}
          />
        </div>
        <div
          className="stripe-form__card-errors"
          id="stripeFormCardErrors"
          role="alert"
        >
          {errorMessage}
        </div>
      </div>
      <button
        id="stripeSubmitButton"
        className={`button ${buttonClassModifier}`}
        disabled={isDisabled}
        aria-label={
          isMakingPayment
            ? localisations.making_payment
            : localisations.make_payment
        }
      >
        {isMakingPayment ? (
          <i aria-hidden="true" className="far fa-spinner fa-pulse"></i>
        ) : (
          buttonText
        )}
      </button>
    </form>
  );
}

export default CardForm;
